<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M7 (7.1).
 * Inbox-side Eva picker: assign (or clear) the Eva assistant that answers this inbox, from the inbox
 * settings page (the other half of binding — D22). Reads the account-wide bindings to show the
 * current owner and the account's assistants as options; writes via the assistant-nested bind/unbind.
 * One-assistant-per-inbox is enforced server-side; switching unbinds the previous owner first.
 * Tailwind-only; Composition API; self-contained so it mounts under vitest.
 */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaAssistantsAPI from 'dashboard/api/eva/assistants';
import EvaInboxesAPI from 'dashboard/api/eva/inboxes';
import EvaInboxBindingsAPI from 'dashboard/api/eva/inboxBindings';
import { evaErrorMessages } from 'dashboard/api/eva/errors';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const assistants = ref([]);
const currentAssistantId = ref(null);
const isSaving = ref(false);
const errorMessage = ref('');

const load = async () => {
  errorMessage.value = '';
  try {
    const [{ data: assistantList }, { data: bindings }] = await Promise.all([
      EvaAssistantsAPI.get(),
      EvaInboxBindingsAPI.get(),
    ]);
    assistants.value = assistantList;
    const binding = bindings.find(b => b.inbox_id === props.inbox.id);
    currentAssistantId.value = binding ? binding.eva_assistant_id : null;
  } catch {
    errorMessage.value = t('EVA_INBOXES.LOAD_ERROR');
  }
};

const onSelect = async event => {
  const next = event.target.value ? Number(event.target.value) : null;
  if (next === currentAssistantId.value) return;
  isSaving.value = true;
  errorMessage.value = '';
  const previous = currentAssistantId.value;
  try {
    if (previous) await EvaInboxesAPI.unbind(previous, props.inbox.id);
    if (next) await EvaInboxesAPI.bind(next, props.inbox.id);
    currentAssistantId.value = next;
  } catch (error) {
    // Resync to the true server state FIRST (a switch unbinds before it binds, so a failed bind can
    // leave the inbox unbound) — load() clears errorMessage, so set the message after it, not before.
    await load();
    errorMessage.value =
      evaErrorMessages(error).join(' ') || t('EVA_INBOXES.SAVE_ERROR');
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);

defineExpose({ load, onSelect });
</script>

<template>
  <div class="flex flex-col gap-2 py-4">
    <label
      class="text-sm font-medium text-n-slate-12"
      for="eva-assistant-select"
    >
      {{ t('EVA_INBOXES.INBOX_SIDE_LABEL') }}
    </label>
    <p class="text-xs text-n-slate-11">
      {{ t('EVA_INBOXES.INBOX_SIDE_HINT') }}
    </p>
    <select
      id="eva-assistant-select"
      class="max-w-md px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
      :value="currentAssistantId ?? ''"
      :disabled="isSaving"
      @change="onSelect"
    >
      <option value="">{{ t('EVA_INBOXES.NOT_CONNECTED') }}</option>
      <option
        v-for="assistant in assistants"
        :key="assistant.id"
        :value="assistant.id"
      >
        {{ assistant.name }}
      </option>
    </select>
    <p v-if="errorMessage" class="text-sm text-n-ruby-11">{{ errorMessage }}</p>
  </div>
</template>
